/**
 * @module app.cm
 */

import { autoinject, singleton, observable, TaskQueue } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, SortEntry } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CaseService, ICaseSearchFilters } from 'app/cm/services/case-service';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { IMemberSearchFilters } from 'app/cd/services/member-service';
import { ManageCasesPageContext } from 'app/cm/lib/manage-cases-page-context';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { ICaseSearchOptions } from 'app/cm/components/case/case-filters';
import { caseIsReadOnly } from 'app/cm/lib/utilities';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { guid } from 'app/shared/lib/guid';
import { EssTableCustomElement, SelectEvent } from 'app/shared/elements/ess-table';
import { confirmation, ConfirmationOptions, IConfirmationDialogResult } from 'app/shared/elements/ess-confirm';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { I18N } from 'aurelia-i18n';
import { getReference } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { requiredFields as memberRequiredFields } from 'app/cd/elements/members/member-display';
import * as CM from 'app/cm/models/essette-api';

@singleton()
@autoinject()
export class CaseSearchPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected caseSearchOptions: Partial<ICaseSearchOptions> = {};
    protected caseSearchFilters: ICaseSearchFilters | null = null;
    protected memberSearchFilters: IMemberSearchFilters | null = null;
    protected tempCaseSearchFilters: ICaseSearchFilters | null = null;
    protected tempMemberSearchFilters: IMemberSearchFilters | null = null;

    @observable
    protected selectedStatus: CM.CaseStatus | null = null;
    protected selectedStatusReason: CM.CaseStatusReason | null = null;
    protected statuses: ReadonlyArray<CM.CaseStatusWithReasonModel> = new Array();
    protected reasons: ReadonlyArray<CM.CaseStatusReason> | null = null;
    protected programGroups: ReadonlyArray<CM.ProgramGroup> = new Array();
    protected acuities: ReadonlyArray<CM.Acuity> = new Array();
    protected intensities: ReadonlyArray<CM.Intensity> = new Array();

    protected selectedCases: CM.Case[] = [];
    protected caseResults: CM.Case[] = [];
    protected $isValid!: boolean;
    protected validateForm = validate;
    protected editedCase: CM.Case | null = null;
    protected summaryCase: CM.Case | null = null;

    private sorting: Sorting = new Sorting();
    protected defaultSortEntry?: SortEntry;
    protected resultCount: number = 0;
    protected pageSize: number = 20;
    protected hasIneligibleMembers: boolean = false;
    protected showJumpToCreate: boolean = false;
    protected enableBulkAction: boolean = false;
    protected selectAllTooltip: string = '';
    protected selectAtLeastOne: string = '';
    protected selectedSingular: string = '';
    protected selectedPlural: string = '';

    protected hasBulkChangeStatus: boolean = false;
    protected hasCaseOwner: boolean = false;
    protected hasCareTeam: boolean = false;
    protected hasCreateAdditional: boolean = false;
    protected hasCreateCaseRoute: boolean = false;
    protected hasCaseRoute: boolean = false;
    protected showCaseSummaryLink: boolean = false;
    protected hasHouseholdCaseRoute: boolean = false;
    private hasCaseEdit: boolean = false;
    private hasEditClosedCase: boolean = false;
    protected openCaseSummary = openCaseSummary;

    protected showProviderSummaryLink: boolean = false;
    protected openProviderSummary = openProviderSummary;
    protected editingCaseNumber: string | null | undefined = "";

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    private $collapsible!: ICollapse;
    protected $table!: EssTableCustomElement<CM.Case>;
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: ManageCasesPageContext,
        private readonly caseService: CaseService,
        private readonly toaster: Toaster,
        private readonly queue: TaskQueue,
        private readonly i18n: I18N,
        protected readonly transitional: TransitionalManager,
        ...rest: any[]) {

        super(...rest);
        this.selectAllTooltip = this.i18n.tr('ui:actions.selectAllOnPage', { what: this.i18n.tr('cm:case.$_plural').toLowerCase() });
        this.selectAtLeastOne = this.i18n.tr('ui:messages.info.selectAtLeastOne', { what: this.i18n.tr('cm:case.$').toLowerCase() });
        this.selectedSingular = this.i18n.tr('ui:messages.info.selected', { what: this.i18n.tr('cm:case.$').toLowerCase(), number: this.selectedCases.length });
        this.selectedPlural = this.i18n.tr('ui:messages.info.selected', { what: this.i18n.tr('cm:case.$_plural').toLowerCase(), number: this.selectedCases.length });
    }

    protected async canActivate(): Promise<boolean> {
        await this.checkPermissions();

        return true;
    }

    protected async activate(): Promise<void> {
        await this.loadSettings();

        if(this.hasCaseOwner) this.submitCaseSearch();

        this.enableBulkAction = false;
        this.loadLookups();
    }

    ////****************** Events ***************************************************////
    protected selectedStatusChanged(newValue: CM.CaseStatus, oldValue: CM.CaseStatus) {
        if(newValue && newValue.id && this.statuses) {
            const foundStatus = this.statuses.find(s => s.id === newValue.id);
            this.reasons = null;
            if(foundStatus && foundStatus.reasons) {
                this.reasons = foundStatus.reasons;
                this.selectedStatusReason = null;
            }
        }
        else {
            this.reasons = null;
        }
        this.selectedStatusReason = null;
    }


    protected handleEnableBulkAction(): void {
        if(this.enableBulkAction) {
            this.$collapsible.open(0);
        }

        this.clearBulkAction();
    }

    protected async handleBulkUpdate(): Promise<void> {
        const isSuccess = await this.caseBulkUpdate();

        if(isSuccess) await this.searchCases();
    }

    protected selectChanged({ detail }: SelectEvent<CM.Case>): void {
        // if item is missing - a select all event occurred
        if(!detail.item) {
            // unselect all items
            if(detail.items.length === 0) {
                this.caseResults.forEach(caseResult => {
                    const index = this.selectedCases.findIndex(
                        selected => selected.id === caseResult.id
                    );
                    if(index !== -1) {
                        this.selectedCases.splice(index, 1);
                    }
                });
            } else {
                // save all items
                detail.items.forEach(item => {
                    const alreadySelected = this.selectedCases.some(
                        selectedCase => selectedCase.id === item.id
                    );
                    if(!alreadySelected) {
                        this.selectedCases.push(item);
                    }
                });
            }
        } else {
            // save selected checkboxes to restore later logic
            const match = detail.items.some(
                item => item.id === detail.item?.id
            );
            // Check for matching id in detail (allows dups and id's correct service)
            if(match) {
                // add to selectedCases if not already present
                const alreadySelected = this.selectedCases.some(
                    selectedCase => selectedCase.id === detail.item?.id
                );
                if(!alreadySelected) {
                    this.selectedCases.push(detail.item!);
                }
            } else {
                // remove from selectedCases if it exists based on the id of detail
                const index = this.selectedCases.findIndex(
                    selectedCase => selectedCase.id === detail.item?.id
                );
                if(index !== -1) {
                    this.selectedCases.splice(index, 1);
                }
            }
        }

        this.refreshTexts();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        this.clearFilters();

        await this.accountContext.load();
        // TODO: [EN] Permission not been created yet, BulkUpdate action needs to be created for permission.
        this.hasBulkChangeStatus = true;

        this.hasCaseOwner = this.accountContext.hasPermission('CM.Case.Duty.Owner');
        this.hasCareTeam = this.accountContext.hasRouteAccess('cm.case.careteam');
        this.hasCreateAdditional = this.accountContext.hasPermission('CM.Case.Duty.Additional');
        this.hasCreateCaseRoute = this.accountContext.hasRouteAccess('cm.manage-cases.create');
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.hasHouseholdCaseRoute = this.accountContext.hasRouteAccess('cm.household-case');

        this.hasCaseEdit = this.accountContext.hasPermission('CM.Case.Update.EditCaseDetails') || this.accountContext.hasPermission('CM.Case.Assign');
        this.hasEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');

        this.caseSearchOptions.mine = { isVisible: this.hasCaseOwner, isLocked: false };
        this.caseSearchOptions.myCareTeam = { isVisible: this.hasCareTeam, isLocked: false };
        this.caseSearchOptions.careTeamParticipant = { isVisible: this.hasCareTeam, isLocked: false };

        this.tempCaseSearchFilters = {
            mine: this.hasCaseOwner && this.env.config.pageConfigurations.cm.manageCases.search.myCasesCaseOwnerChecked,
            myCareTeam: this.hasCaseOwner && this.env.config.pageConfigurations.cm.manageCases.search.myCareTeamCasesChecked,
            openOnly: this.hasCaseOwner && this.env.config.pageConfigurations.cm.manageCases.search.onlyOpenCasesChecked,
            closedOnly: this.hasCaseOwner && this.env.config.pageConfigurations.cm.manageCases.search.onlyClosedCasesChecked && !this.env.config.pageConfigurations.cm.manageCases.search.onlyOpenCasesChecked
        }

        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    protected canEditCase($case: CM.Case): boolean {
        if(!$case) { return false; }
        const readonly = caseIsReadOnly($case, this.env.user.id);
        return (!readonly && this.hasCaseEdit) || (readonly && this.hasEditClosedCase);
    }

    protected editCase($case: CM.Case): void {
        if(this.canEditCase($case)) {
            this.editedCase = $case;
            this.editingCaseNumber = $case.number;
            this.$modal.open();
        }
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        if(!!this.$modal) {
            this.$modal.close();
            this.searchCases();
        }
    }

    /** Copy member search criteria and navigate to Create Case */
    protected jumpToCreate(): void {
        this.context.memberSearchFilters = this.memberSearchFilters!;
        if(this.router) {
            this.router.navigateToRoute('cm.manage-cases.create', { searchMode: true });
        }
    }

    private restoreSelected() {
        if(!(this.selectedCases && this.selectedCases.length > 0)) return;
        // Give aurelia a chance to bind data to the table before we ask the table to restore selected items
        this.queue.queueTask(() => {
            this.$table.restoreSelected(this.selectedCases);
        });
    }

    protected clearBulkAction(): void {
        // Deselect first before clearing, else selection is re-added.
        if(this.$table) this.$table.selected = [];

        this.selectedCases = [];
        this.selectedStatus = null;
        this.selectedStatusReason = null;
        this.refreshTexts();
    }


    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        // only trigger search if there is data to sort
        if(this.caseResults.length) await this.searchCases();
    }

    protected clearFilters() {
        this.caseSearchFilters = {};
        this.tempCaseSearchFilters = {};
        this.memberSearchFilters = {};
        this.tempMemberSearchFilters = {};
    }

    protected clearResults() {
        this.caseResults = [];
        if(this.$pager) this.$pager.updatePage(0);
        this.resultCount = 0;
        this.showJumpToCreate = false;
        this.clearBulkAction();
    }

    protected submitCaseSearch() {
        this.clearBulkAction();
        if(this.tempCaseSearchFilters && !!this.tempCaseSearchFilters.mine) {
            this.tempCaseSearchFilters.assignedTo = { id: this.env.user.id, $display: this.env.user.description, description: this.env.user.description };
        }
        if(this.tempCaseSearchFilters && !!this.tempCaseSearchFilters.myCareTeam) {
            this.tempCaseSearchFilters.careTeamParticipant = { id: this.env.user.id, $display: this.env.user.description, description: this.env.user.description };
        }
        this.caseSearchFilters = { ...this.tempCaseSearchFilters };
        this.memberSearchFilters = { ...this.tempMemberSearchFilters };
        this.searchCases();
    }

    protected refreshTexts() {
        this.selectedSingular = this.i18n.tr('ui:messages.info.selected', { what: this.i18n.tr('cm:case.$').toLowerCase(), number: this.selectedCases.length });
        this.selectedPlural = this.i18n.tr('ui:messages.info.selected', { what: this.i18n.tr('cm:case.$_plural').toLowerCase(), number: this.selectedCases.length });
    }

    protected buildBulkUpdateCaseObject(): CM.Case {
        const saveCases = this.selectedCases.map(c => {
            return <CM.Case>getReference(c);
        });

        const saveBulkCase: CM.Case = {
            status: getReference(this.selectedStatus),
            cases: saveCases
        };

        if(!!this.selectedStatusReason && !!saveBulkCase.status) {
            saveBulkCase.status.reason = getReference(this.selectedStatusReason);
        }

        return saveBulkCase;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async searchCases(): Promise<void> {
        if(!(this.caseSearchFilters && this.memberSearchFilters)) { return; }

        // use default sort if no other sort specified
        let sort = new Sorting();
        if(this.sorting.entries.length === 0 && this.defaultSortEntry) sort.entries.push(this.defaultSortEntry);
        else sort = this.sorting;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: sort
        };

        const includes: string[] = [
            'acuity',
            'assignedTo',
            'custom',
            'custom.target',
            'intensity',
            'member',
            'member.primaryCarePhysician',
            'member.primaryPlan.healthPlan',
            'member.primaryPlan.lineOfBusiness',
            'program',
            'program.group',
            'status'
        ];

        const fields: string[] = [
            'acuity.description',
            'assignedTo.description',
            'custom.lookup.*',
            'custom.text.*',
            'intensity.description',
            'isClosed',
            'member.birthDate',
            'member.eligibility.isCurrent',
            'member.primaryCarePhysician.description',
            'member.primaryCarePhysician.name.full',
            'member.primaryPlan.healthPlan.description',
            'member.primaryPlan.lineOfBusiness.description',
            'metadata.created.timestamp',
            'metadata.rollupModified.timestamp',
            'number',
            'program.description',
            'program.group.description',
            'status.description'
        ];

        memberRequiredFields.forEach(field => !fields.includes(`member.${field}`) ? fields.push(`member.${field}`) : null);

        if(this.hasHouseholdCaseRoute) {
            includes.push('householdCase');
            includes.push('householdCase.household');
            fields.push('householdCase.number');
            fields.push('householdCase.household.description');
            fields.push('householdCase.household.identifier.primary');
        }

        try {
            this.state.beginLoad();

            // too many filters can exceed URL character limit, so use filters only to fetch IDs first
            const idResponse = await this.caseService.searchCases(this.caseSearchFilters, this.memberSearchFilters, options, [], ['id']);
            const caseIds: string[] = idResponse.results.map(c => c.id!);
            const fullResponse = await this.caseService.loadCasesById(caseIds, { sorting: sort }, includes, fields);
            this.caseResults = fullResponse.results;
            this.resultCount = idResponse.count || 0;

            // If results include some ineligible members, show the warning column
            this.hasIneligibleMembers = this.caseResults.some(c => { return c.member && c.member.eligibility && !c.member.eligibility.isCurrent; });
            this.showJumpToCreate = this.caseService.countMemberFilters(this.memberSearchFilters) > 0;
            this.restoreSelected();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async caseBulkUpdate(): Promise<Boolean> {
        if(this.selectedCases.length < 1) return false;

        let result: IConfirmationDialogResult = {
            reason: '',
            dialogResponse: ConfirmationOptions.OK
        };

        const messageWhat = this.selectedCases.length === 1 ? `${this.selectedCases.length} ${this.i18n.tr('cm:case.$').toLowerCase()}` : `${this.selectedCases.length} ${this.i18n.tr('cm:case.$_plural').toLowerCase()}`;
        const message: string = this.i18n.tr('ui:messages.actions.update', { what: messageWhat });
        const subMessage: string = this.selectedStatusReason ? this.i18n.tr('cm:local.case.caseStatusChange.statusWithReason', { what: this.selectedStatus?.description, item: this.selectedStatusReason.description }) : this.i18n.tr('cm:local.case.caseStatusChange.status', { what: this.selectedStatus?.description });
        result = await confirmation.showCustom(message, subMessage, confirmation.options.StandardConfirm, false);
        if(!(result.dialogResponse === confirmation.options.OK)) return false;

        const saveBulkCase = this.buildBulkUpdateCaseObject();

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.caseService.bulkUpdateCase(saveBulkCase);

            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.updated('cm:case.$', isSuccessful, this.selectedCases.length);

            if(isSuccessful) this.clearBulkAction();
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    private async loadSettings(): Promise<void> {
        try {
            this.state.beginLoad();

            const response = await this.caseService.loadCaseSettings();
            const settings = response.settings;

            // attempt to configure default sort via attribute
            const defaultSort = getApiSetting(settings, 'defaultCaseSearchSort.$default')?.toString();
            if(!defaultSort) return; // no attribute value found

            const [reference, directionString]: string[] = defaultSort.split(':');
            if(!reference || !['asc', 'desc'].includes(directionString)) return; // attribute value is invalid

            this.defaultSortEntry = {
                reference,
                direction: directionString === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            };
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        if(!this.hasBulkChangeStatus) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const programGroupIncludes = ['programs'];
        const programGroupFields = ['description', 'programs.description'];
        const statusIncludes = ['reasons'];
        const statusFields = ['description', 'reasons.description'];
        const acuitiesFields = ['description'];
        const intensitiesFields = ['description'];

        try {
            this.state.beginLoad();

            const [programGroups, statuses, acuities, intensities] = await Promise.all(
                [
                    this.caseService.loadProgramGroups(options, programGroupIncludes, true, undefined, true, programGroupFields),
                    this.caseService.loadCaseStatuses(options, statusIncludes, undefined, statusFields),
                    this.caseService.loadAcuities(options, undefined, acuitiesFields),
                    this.caseService.loadIntensities(options, undefined, intensitiesFields)
                ]
            );

            this.programGroups = programGroups.results;
            this.statuses = statuses.results;
            this.acuities = acuities.results;
            this.intensities = intensities.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
